import { useState, useEffect, useRef } from 'react';
import { submitAttempt } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './StoryForge.css';

const MAX_GUESSES = 3;

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

export default function StoryForge({ puzzle, onSolve, alreadyAttempted, onPlayAgain, loadingPlayAgain }) {
  const { user, fetchCredits } = useAuth();

  const [guess, setGuess]         = useState('');
  const [guesses, setGuesses]     = useState([]);
  const [hintLevel, setHintLevel] = useState(0);
  const [gameOver, setGameOver]   = useState(false);
  const [correct, setCorrect]     = useState(false);
  const [shareText, setShareText]   = useState('');
  const [elapsed, setElapsed]     = useState(0);

  const startTime = useRef(null);
  const timerRef  = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (!puzzle?.id) return;
    const key = `sf_start_${puzzle.id}`;
    if (!sessionStorage.getItem(key)) sessionStorage.setItem(key, Date.now().toString());
    startTime.current = parseInt(sessionStorage.getItem(key));
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [puzzle?.id]);

  useEffect(() => {
    if (gameOver) clearInterval(timerRef.current);
  }, [gameOver]);

  useEffect(() => {
    setGuess('');
    setGuesses([]);
    setHintLevel(0);
    setGameOver(false);
    setCorrect(false);
    setElapsed(0);
  }, [puzzle?.id]);

  if (!puzzle?.content) return <div className="sf-loading mono">Loading puzzle...</div>;
  if (loadingPlayAgain) return (
    <div className="sf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}>⏳</div>
      <div>✨ Generating new puzzle powered by AI</div>
    </div>
  );
  if (alreadyAttempted) return (
    <div className="sf-loading mono" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}><span style={{color:'initial'}}>✅</span></div>
      <div style={{fontSize:'16px', fontWeight:'700', color:'#f0f4ff', marginBottom:'8px'}}>Already completed today!</div>
      <div style={{fontSize:'12px', color:'rgba(240,244,255,0.5)', marginBottom:'16px'}}>Use 1 credit to play a new puzzle.</div>
      {onPlayAgain
        ? <button onClick={onPlayAgain} style={{fontSize:'12px', color:'#4fe3c1', background:'rgba(79,227,193,0.1)', border:'1px solid rgba(79,227,193,0.35)', borderRadius:'8px', padding:'8px 16px', cursor:'pointer'}}>✨ Play again — 1 credit</button>
        : <a href="/upgrade" style={{fontSize:'12px', color:'#e8c547', textDecoration:'none'}}>Get more credits →</a>
      }
    </div>
  );

  const { story, answer, hints, theme, theme_emoji, difficulty } = puzzle.content;
  const guessesLeft = MAX_GUESSES - guesses.length;

  const handleSubmit = () => {
    if (!guess.trim() || gameOver) return;

    const normalizedGuess = guess.trim().toUpperCase();
    const normalizedAnswer = answer.toUpperCase();
    const isCorrect = normalizedGuess === normalizedAnswer ||
      normalizedAnswer.includes(normalizedGuess) ||
      normalizedGuess.includes(normalizedAnswer);

    const newGuesses = [...guesses, { text: guess.trim(), correct: isCorrect }];
    setGuesses(newGuesses);
    setGuess('');

    const isLastGuess = newGuesses.length >= MAX_GUESSES;

    if (isCorrect || isLastGuess) {
      setCorrect(isCorrect);
      setGameOver(true);
      const th = puzzle?.content?.theme_emoji && puzzle?.content?.theme ? `${puzzle.content.theme_emoji} ${puzzle.content.theme} Week\n` : '';
      const timeTakenSF = Math.floor((Date.now() - startTime.current) / 1000);
      const mins = Math.floor(timeTakenSF/60); const secs = timeTakenSF%60;
      const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      const tries = newGuesses.length;
      setShareText(`${th}📖 StoryForge\n${isCorrect ? `Solved in ${tries} tr${tries===1?'y':'ies'} · ${timeStr} ✅` : `Not solved · ${timeStr} ❌`}\n\n🎮 Play today → puzzleforge.io`);
      const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
      if (user && puzzle) {
        submitAttempt({
          puzzle_id: puzzle.id,
          solved: isCorrect,
          num_guesses: newGuesses.length,
          guesses: newGuesses.map(g => g.text),
          score: isCorrect ? Math.max(10, 100 - (newGuesses.length - 1) * 30) : 0,
          time_taken_seconds: timeTakenSF,
        }).then(() => { if (fetchCredits) fetchCredits(); }).catch(console.error);
      }
      if (isCorrect && onSolve) onSolve(isCorrect);
    } else {
      // Reveal next hint on wrong guess
      setHintLevel(h => Math.min(h + 1, hints?.length || 0));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // Render story with blank highlighted
  const renderStory = () => {
    const parts = story.split('___');
    return parts.map((part, i) => (
      <span key={i}>
        {part}
        {i < parts.length - 1 && (
          <span className="sf-blank mono">
            {gameOver && correct ? answer : '___'}
          </span>
        )}
      </span>
    ));
  };

  return (
    <div className="sf-wrap">
      {/* Header */}
      <div className="sf-header">
        <div className="sf-label">
          <span className="sf-emoji">📖</span>
          <div>
            <span className="sf-tag mono">{theme_emoji} {theme}</span>
            <span className="sf-title mono">StoryForge</span>
          </div>
        </div>
        <div className="sf-header-right">
          <span className="sf-timer mono">⏱ {formatTime(elapsed)}</span>
          <span className={`sf-difficulty mono ${difficulty}`}>{difficulty}</span>
        </div>
      </div>

      <p className="sf-instruction mono">Read the story and fill in the blank.</p>

      {/* Story */}
      <div className="sf-story">
        {renderStory()}
      </div>

      {/* Hints */}
      {hintLevel > 0 && hints?.length > 0 && (
        <div className="sf-hints">
          {hints.slice(0, hintLevel).map((hint, i) => (
            <div key={i} className="sf-hint mono">
              <span className="sf-hint-num">{i + 1}</span>
              {hint}
            </div>
          ))}
        </div>
      )}

      {/* Previous guesses */}
      {guesses.length > 0 && (
        <div className="sf-guesses">
          {guesses.map((g, i) => (
            <div key={i} className={`sf-guess mono ${g.correct ? 'correct' : 'wrong'}`}>
              <span>{g.correct ? '✓' : '✗'}</span>
              <span>{g.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tries dots */}
      <div className="sf-tries-dots">
        {Array.from({length: MAX_GUESSES}).map((_, i) => (
          <div key={i} className={`sf-try-dot ${
            i < guesses.length
              ? guesses[i].correct ? 'correct' : 'wrong'
              : ''
          }`} />
        ))}
      </div>

      {/* Result */}
      {gameOver && (
        <div className={`sf-result ${correct ? 'correct' : 'wrong'}`}>
          <div className="sf-result-icon">{correct ? '🎉' : '😔'}</div>
          <div className="sf-result-text mono">
            {correct
              ? `Solved in ${guesses.length} tr${guesses.length === 1 ? 'y' : 'ies'} · ${formatTime(elapsed)}`
              : `The answer was: ${answer}`}
          </div>
          {shareText && (
            <button className="sf-share mono" onClick={() => navigator.clipboard.writeText(shareText).catch(()=>{})}>
              📋 Copy Result
            </button>
          )}
        </div>
      )}

      {/* Input */}
      {!gameOver && (
        <div className="sf-input-wrap">
          <div className="sf-tries-left mono">{guessesLeft} tr{guessesLeft === 1 ? 'y' : 'ies'} left</div>
          <div className="sf-input-row">
            <input
              ref={inputRef}
              className="sf-input mono"
              value={guess}
              onChange={e => setGuess(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your answer..."
              autoComplete="off"
              autoCapitalize="words"
            />
            <button
              className="sf-submit mono"
              onClick={handleSubmit}
              disabled={!guess.trim()}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
